/**
 * Account Locked Page - Trade Loop
 *
 * Displayed when a user with a locked account attempts to log in.
 * Shows the lock reason and provides a form to request account unlock.
 */

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Lock, CheckCircle, Clock, ShieldAlert } from "lucide-react";
import { useToast } from "@/contexts/ToastContext";
import tradeLoopLogo from "@/assets/trade-loop-logo.svg";

interface LockDetails {
  reason: string | null;
  details: string | null;
  unlockRequirements: string | null;
  expiresAt: string | null;
  requiresAdminApproval: boolean;
}

interface LocationState {
  email?: string;
  lockDetails?: LockDetails;
}

export default function AccountLockedPage() {
  const location = useLocation();
  const { showSuccess, showError } = useToast();

  const state = location.state as LocationState | null;
  const email = state?.email || "";
  const lockDetails = state?.lockDetails;

  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Format the reason for display
  const formatReason = (reason: string | null) => {
    if (!reason) return "Policy violation";
    const reasonMap: Record<string, string> = {
      terms_violation: "Terms of Service Violation",
      suspicious_activity: "Suspicious Activity Detected",
      payment_issue: "Payment or Billing Issue",
      user_request: "User Requested Lock",
      security_concern: "Security Concern",
      spam_abuse: "Spam or Abuse",
      other: "Other",
    };
    return reasonMap[reason] || reason;
  };

  // Format expiration date
  const formatExpiration = (expiresAt: string | null) => {
    if (!expiresAt) return null;
    const date = new Date(expiresAt);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !message.trim()) {
      showError("Please enter a message", "Explain why your account should be unlocked");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || "http://localhost:3000"}/api/account/unlock-request`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            message: message.trim(),
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to submit request");
      }

      setIsSubmitted(true);
      showSuccess("Request Submitted", "An administrator will review your request shortly");
    } catch (error: any) {
      showError("Failed to submit request", error.message || "Please try again later");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state after submitting unlock request
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50/30 to-white">
        <header className="px-4 py-8 pt-[calc(1.5rem+env(safe-area-inset-top))] md:pt-8">
          <div className="flex items-center justify-center max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
            <img src={tradeLoopLogo} alt="Trade Loop" className="h-10 w-auto" />
          </div>
        </header>

        <main className="px-4 py-8">
          <div className="max-w-md mx-auto">
            <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0 rounded-2xl animate-in slide-in-from-bottom-4 duration-500">
              <CardContent className="px-6 py-10 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-in zoom-in duration-300 delay-150">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">
                  Request Submitted
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Your unlock request has been submitted. An administrator will review it and contact
                  you at <span className="font-semibold text-gray-900">{email}</span> with their
                  decision.
                </p>
                <div className="space-y-3">
                  <Link to="/login">
                    <Button className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02]">
                      Back to Sign In
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button
                      variant="outline"
                      className="w-full h-12 rounded-xl font-semibold text-base"
                    >
                      Contact Support
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50/30 to-white">
      {/* Header */}
      <header className="px-4 py-8 pt-[calc(1.5rem+env(safe-area-inset-top))] md:pt-8">
        <div className="flex items-center justify-between max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
          <Link to="/login">
            <Button
              variant="ghost"
              size="sm"
              className="p-3 hover:bg-white/60 rounded-xl backdrop-blur-sm border border-white/20 transition-all duration-200"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </Button>
          </Link>
          <img src={tradeLoopLogo} alt="Trade Loop" className="h-10 w-auto" />
          <div className="w-11"></div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-8">
        <div className="max-w-md mx-auto space-y-6">
          {/* Lock Status Card */}
          <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0 rounded-2xl animate-in slide-in-from-bottom-4 duration-500 overflow-hidden">
            {/* Warning Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Account Locked</h1>
                  <p className="text-amber-100 text-sm">Access has been restricted</p>
                </div>
              </div>
            </div>

            <CardContent className="px-6 py-6">
              <div className="space-y-4">
                <p className="text-gray-600 leading-relaxed">
                  Your account has been locked by a system administrator. You cannot access your
                  account until it has been unlocked.
                </p>

                {/* Lock Details */}
                {lockDetails && (
                  <div className="space-y-3 border-t border-gray-100 pt-4">
                    {/* Reason */}
                    <div className="flex items-start gap-3">
                      <ShieldAlert className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Reason</p>
                        <p className="text-gray-900 font-semibold">
                          {formatReason(lockDetails.reason)}
                        </p>
                      </div>
                    </div>

                    {/* Details */}
                    {lockDetails.details && (
                      <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                        <p className="text-sm text-amber-800">{lockDetails.details}</p>
                      </div>
                    )}

                    {/* Expiration */}
                    {lockDetails.expiresAt && (
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Lock Expires</p>
                          <p className="text-gray-900">{formatExpiration(lockDetails.expiresAt)}</p>
                        </div>
                      </div>
                    )}

                    {/* Unlock Requirements */}
                    {lockDetails.unlockRequirements && (
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                        <p className="text-sm font-medium text-blue-800 mb-1">
                          To unlock your account:
                        </p>
                        <p className="text-sm text-blue-700">{lockDetails.unlockRequirements}</p>
                      </div>
                    )}

                    {/* Admin Approval Notice */}
                    {lockDetails.requiresAdminApproval && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <ShieldAlert className="w-4 h-4" />
                        <span>Administrator approval required to unlock</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Unlock Request Form */}
          <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0 rounded-2xl animate-in slide-in-from-bottom-4 duration-500 delay-150">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-bold text-gray-900">
                Request Account Unlock
              </CardTitle>
              <p className="text-gray-600 text-sm">
                Submit a request to have your account reviewed by an administrator.
              </p>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-sm font-semibold text-gray-700">
                    Your Message
                  </Label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Explain why you believe your account should be unlocked..."
                    className="min-h-[120px] bg-white/80 backdrop-blur-sm border border-gray-200/50 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl text-base font-medium transition-all duration-200 resize-none"
                    required
                    disabled={isSubmitting}
                  />
                  <p className="text-xs text-gray-500">
                    Provide context about your situation. This will help the administrator make a
                    decision.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={!message.trim() || isSubmitting}
                  className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white font-semibold text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none disabled:shadow-none"
                >
                  {isSubmitting ? "Submitting..." : "Submit Unlock Request"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Help Links */}
          <div className="text-center space-y-2">
            <p className="text-sm text-gray-500">Need immediate assistance?</p>
            <Link
              to="/contact"
              className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors duration-200"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
